export default {
  computed: {
    currentTouranment () {
      return this.$route.params.id ? this.$store.getters['tournaments/get'](this.$route.params.id) : this.$store.state.app.activeTournament
    },

    /** Tournament used for fee calculation. Prefer prop (passed to RegistrationFee/PrintRegistrationFee) so fee fields are always available. */
    tournamentForFees () {
      return this.tournament != null ? this.tournament : this.currentTouranment
    }
  },

  methods: {
    getAllDisciplines (registration) {
      if (!registration?.categories?.length) return []
      return registration.categories.reduce((acc, item) => {
        acc.push(...(item.disciplines || []))
        return acc
      }, [])
    },

    getNoPayDisciplines (registration) {
      const allDisciplines = this.getAllDisciplines(registration)

      const hasKateA = allDisciplines.filter(d => d.discipline.isKataA)
      const hasKateB = allDisciplines.some(d => d.discipline.isKataB)

      if (hasKateA.length && hasKateB) {
        return hasKateA.map(d => d.disciplineId)
      }

      return []
    },

    /** Disciplines flagged "always pay" — these are charged even to a fee-exempt competitor/team. */
    getAlwaysPayDisciplinesCount (registration) {
      return this.getAllDisciplines(registration).filter(d => d.discipline?.alwaysPay).length
    },

    getRegistrationDisciplinesCount (registration) {
      if (!registration?.categories?.length) return 0

      const chargeAll = !!this.tournamentForFees?.chargeAllDiscplines
      const feeExempt = registration.isTeam
        ? (registration.noPay && !chargeAll)
        : !!registration.competitor?.noPay

      // Fee exemption on the competitor/team only covers disciplines that are not flagged "always pay".
      if (feeExempt) return this.getAlwaysPayDisciplinesCount(registration)

      if (chargeAll) {
        return registration.categories.reduce((acc, item) => acc + (item.disciplines?.length || 0), 0)
      }

      const noPayIds = registration.isTeam ? [] : this.getNoPayDisciplines(registration)

      const competitor = registration.competitor
      return registration.categories
        .filter(item => (registration.isTeam && registration.alwaysPays) || competitor?.alwaysPays || item.disciplines?.some(d => d.discipline?.alwaysPay) || !item.category?.noPay)
        .reduce((acc, item) => (item.disciplines || []).filter(d => {
          return (registration.isTeam && registration.alwaysPays) || competitor?.alwaysPays || d.discipline?.alwaysPay || !(noPayIds.includes(d.discipline?.id) || d.discipline?.noPay)
        }).length + acc, 0)
    },

    /** For bundle type: rule that applies to this discipline count (highest min_disciplines <= count). Rules must be sorted by min_disciplines ascending. */
    getBundleRuleForDisciplineCount (disciplineCount) {
      const rules = this.tournamentForFees?.feeRules || []
      const sorted = [...rules].sort((a, b) => (b.min_disciplines ?? 0) - (a.min_disciplines ?? 0))
      return sorted.find(r => (r.min_disciplines ?? 0) <= disciplineCount) || null
    },

    getPrice (registration, costReduction) {
      const t = this.tournamentForFees
      if (!registration?.competitor) return 0
      const disciplines = this.getRegistrationDisciplinesCount(registration)
      if (disciplines === 0) return 0

      const feeType = t?.registration_fee_type || 'legacy'

      if (feeType === 'bundle') {
        const rule = this.getBundleRuleForDisciplineCount(disciplines)
        if (!rule) return 0
        const price = rule.price ?? 0
        const priceBreak = costReduction ? (rule.price_break ?? 0) : 0
        return price - priceBreak
      }

      if (feeType === 'per_discipline') {
        const price = (t?.price_per_discipline ?? 0) * disciplines
        const priceBreak = costReduction ? (t?.price_per_discipline_break ?? 0) * disciplines : 0
        return price - priceBreak
      }

      if (disciplines === 1) return (t?.single_fee_1 ?? 0) - (costReduction ? (t?.single_fee_1_break ?? 0) : 0)
      if (disciplines === 2) return (t?.single_fee_2 ?? 0) - (costReduction ? (t?.single_fee_2_break ?? 0) : 0)
      if (disciplines === 3) return (t?.single_fee_3 ?? 0) - (costReduction ? (t?.single_fee_3_break ?? 0) : 0)
      return (t?.single_fee_other ?? 0) - (costReduction ? (t?.single_fee_other_break ?? 0) : 0)
    },

    getTeamPrice (registration, costReduction) {
      const t = this.tournamentForFees
      const disciplines = this.getRegistrationDisciplinesCount(registration)
      if (disciplines === 0) return 0

      const feeType = t?.registration_fee_type || 'legacy'

      if (feeType === 'bundle' || feeType === 'per_discipline') {
        const fee = t?.team_discipline_fee ?? 0
        const feeBreak = costReduction ? (t?.team_discipline_fee_break ?? 0) : 0
        return (fee * disciplines) - (feeBreak * disciplines)
      }

      return ((t?.team_fee ?? 0) * disciplines) - (costReduction ? ((t?.team_fee_break ?? 0) * disciplines) : 0)
    },

    getPriceText (registration, hideDiscount) {
      const t = this.tournamentForFees
      if (!registration?.competitor) return '0'
      const disciplines = this.getRegistrationDisciplinesCount(registration)

      if (disciplines === 0) return '0'

      const feeType = t?.registration_fee_type || 'legacy'

      if (feeType === 'bundle') {
        const rule = this.getBundleRuleForDisciplineCount(disciplines)
        if (!rule) return 0
        const price = rule.price ?? 0
        const priceBreak = hideDiscount ? 0 : (rule.price_break ?? 0)
        if (hideDiscount) return `${price}`
        return `${price} - ${priceBreak} = ${price - priceBreak}`
      }

      if (feeType === 'per_discipline') {
        const price = (t?.price_per_discipline ?? 0) * disciplines
        const priceBreak = hideDiscount ? 0 : (t?.price_per_discipline_break ?? 0) * disciplines
        if (hideDiscount) return `${price}`
        return `${price} - ${priceBreak} = ${price - priceBreak}`
      }

      if (hideDiscount) return `${t?.single_fee_1 ?? 0}`

      if (disciplines === 1) return `${t?.single_fee_1 ?? 0} - ${t?.single_fee_1_break ?? 0} = ${(t?.single_fee_1 ?? 0) - (t?.single_fee_1_break ?? 0)}`
      if (disciplines === 2) return `${t?.single_fee_2 ?? 0} - ${t?.single_fee_2_break ?? 0} = ${(t?.single_fee_2 ?? 0) - (t?.single_fee_2_break ?? 0)}`
      if (disciplines === 3) return `${t?.single_fee_3 ?? 0} - ${t?.single_fee_3_break ?? 0} = ${(t?.single_fee_3 ?? 0) - (t?.single_fee_3_break ?? 0)}`
      return `${t?.single_fee_other ?? 0} - ${t?.single_fee_other_break ?? 0} = ${(t?.single_fee_other ?? 0) - (t?.single_fee_other_break ?? 0)}`
    },

    getTeamPriceText (registration, hideDiscount) {
      const t = this.tournamentForFees
      const disciplines = this.getRegistrationDisciplinesCount(registration)

      if (disciplines === 0) return 0

      const feeType = t?.registration_fee_type || 'legacy'

      if (feeType === 'bundle' || feeType === 'per_discipline') {
        const fee = t?.team_discipline_fee ?? 0
        const feeBreak = t?.team_discipline_fee_break ?? 0
        const totalPrice = fee * disciplines
        const totalBreak = hideDiscount ? 0 : feeBreak * disciplines
        if (hideDiscount) return `${totalPrice}`
        return `${totalPrice} - ${totalBreak} = ${totalPrice - totalBreak}`
      }

      const fee = t?.team_fee ?? 0
      const feeBreak = t?.team_fee_break ?? 0
      const totalPrice = fee * disciplines
      const totalBreak = hideDiscount ? 0 : feeBreak * disciplines
      if (hideDiscount) return `${totalPrice}`
      return `${totalPrice} - ${totalBreak} = ${totalPrice - totalBreak}`
    }
  }
}
